// src/components/landingpage/downloads/DownloadsPage.jsx
//
// The Downloads page (website design, 4 Oct 2026). The elements are fixed; the
// text inside them is fetched from the app's own data file on GitHub and
// rendered as it arrives (data note v1 §3–§4):
//
//   title, description            → heading and intro
//   details[]                     → the table, one row per entry, any length
//   whats_new, install, verify    → their headings arrive ready to display
//   download_url                  → the Download button, a plain link
//
// Every value is inserted as text, never as HTML. If the file cannot be read
// the Download button still works from the stable address, the built-in intro
// and install steps are shown, and no error is put in front of the visitor.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import KuestionMark from '../../kuestion/KuestionMark';
import TerminalXMark from '../pp-terminalx/TerminalXMark';
import { DOWNLOAD_APPS } from '../../../config/downloads';
import { loadDownload } from './downloadsFeed';
import './DownloadsPage.css';

const pad2 = (n) => String(n).padStart(2, '0');
const byKey = (rows, key) => (rows.find((r) => r.key === key) || {}).value || '';
// "Noticeboard: a megaphone button…" → the lead-in set in ink, the rest as is.
const lead = (text) => {
  const i = text.indexOf(': ');
  return i > 0 && i <= 40 ? [text.slice(0, i + 1), text.slice(i + 1)] : ['', text];
};
// Only used when the table carries no display-ready release date.
const dateFrom = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  try { return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(d); } catch { return ''; }
};
// The values that are identifiers rather than prose: wrap them anywhere.
const BREAK_KEYS = new Set(['file', 'package']);

// The mark beside each app's title, by the `icon` named in config/downloads.
// Inline SVG only: this page never renders an <img>, so feed text can be
// proven to stay text.
const Mark = ({ icon }) => {
  if (icon === 'terminalx') return <span className="rw-dl__tile rw-dl__tile--plain"><TerminalXMark /></span>;
  return <span className="rw-dl__tile"><KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" /></span>;
};

function VerifyBox({ verify }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = (row) => {
    try { if (navigator.clipboard) navigator.clipboard.writeText(row.value); } catch { /* not available */ }
    setCopied(row.key || row.label);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="rw-dl__verify">
      <button type="button" className="rw-dl__verify-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 4 6.5v5c0 4.8 3.3 8.6 8 9.5 4.7-.9 8-4.7 8-9.5v-5z" /><polyline points="9 12 11 14 15 10" /></svg>
        <span>{verify.heading || 'Verify your download'}</span>
        <svg className={`rw-dl__chev${open ? ' is-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="rw-dl__verify-body">
          {verify.items.map((row) => (
            <div key={row.key || row.label}>
              <div className="rw-dl__verify-row">
                <span>{row.label}</span>
                <button type="button" onClick={() => copy(row)} aria-label={`Copy the ${row.label}`}>{copied === (row.key || row.label) ? 'Copied' : 'Copy'}</button>
              </div>
              <code>{row.value}</code>
            </div>
          ))}
          {verify.note && <p>{verify.note}</p>}
        </div>
      )}
    </div>
  );
}

function AppCard({ app }) {
  // null until the file is read, and null again if it cannot be: either way
  // the card below is complete and its button works.
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    loadDownload(app).then((d) => { if (live) setData(d); });
    return () => { live = false; };
  }, [app]);

  const fb = app.fallback;
  const title = (data && data.title) || fb.title;
  const description = (data && data.description) || fb.description;
  const url = (data && data.download_url) || fb.download_url;
  const details = data ? data.details : [];
  const install = data && data.install.steps.length ? data.install : fb.install;
  const whatsNew = data && data.whats_new.items.length ? data.whats_new : null;
  const verify = data && data.verify.items.length ? data.verify : null;

  const version = data ? (data.version || byKey(details, 'version')) : '';
  const released = data ? (byKey(details, 'released') || dateFrom(data.released_at)) : '';
  const size = byKey(details, 'size');
  const under = [byKey(details, 'file'), byKey(details, 'works_on')].filter(Boolean).join(' · ');

  return (
    <article id={app.id} className={`rw-dl__app${details.length || verify ? '' : ' is-single'}`}>
      <div className="rw-dl__main">
        <div className="rw-dl__id">
          <Mark icon={app.icon} />
          <div style={{ minWidth: 0 }}>
            <h3>{title}</h3>
            {(version || released) && (
              <div className="rw-dl__tags">
                {version && <span className="rw-tag rw-tag--accent">Version {version}</span>}
                {released && <span className="rw-tag rw-tag--neutral">{released}</span>}
              </div>
            )}
          </div>
        </div>

        <p className="rw-dl__desc">{description}</p>

        <div className="rw-dl__get">
          {/* A plain link, not a fetch: it redirects to the file store and the browser downloads it. */}
          <a href={url} className="rw-btn rw-btn--primary rw-dl__button" rel="noopener">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11" /><polyline points="7 10 12 15 17 10" /><path d="M4 19h16" /></svg>
            {size ? `Download · ${size}` : 'Download'}
          </a>
          {under && <span className="rw-dl__under">{under}</span>}
          {app.about && <Link to={app.about.to} className="rw-dl__about">{app.about.label} &rarr;</Link>}
        </div>

        {whatsNew && (
          <div className="rw-dl__block">
            <h4>{whatsNew.heading || 'What’s new'}</h4>
            <ul className="rw-dl__news">
              {whatsNew.items.map((item, i) => {
                const [head, rest] = lead(item);
                return <li key={i}>{head && <strong>{head}</strong>}{rest}</li>;
              })}
            </ul>
          </div>
        )}

        <div className="rw-dl__block">
          <h4>{install.heading || 'How to install'}</h4>
          <ol className="rw-dl__steps">
            {install.steps.map((step, i) => (
              <li key={i}><span aria-hidden="true">{pad2(i + 1)}</span><span>{step}</span></li>
            ))}
          </ol>
        </div>
      </div>

      {(details.length > 0 || verify) && (
        <div className="rw-dl__side">
          {details.length > 0 && (
            <dl className="rw-dl__table">
              {details.map((row) => (
                <div key={row.key || row.label}>
                  <dt>{row.label}</dt>
                  <dd className={BREAK_KEYS.has(row.key) ? 'is-break' : undefined}>{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {verify && <VerifyBox verify={verify} />}
        </div>
      )}
    </article>
  );
}

export default function DownloadsPage() {
  useEffect(() => {
    const before = document.title;
    document.title = 'Downloads · Rift Works';
    return () => { document.title = before; };
  }, []);

  // Grouped by platform, in the order the apps are listed; counts are derived.
  const platforms = DOWNLOAD_APPS.reduce((acc, app) => {
    const g = acc.find((x) => x.name === app.platform);
    if (g) g.apps.push(app); else acc.push({ name: app.platform, apps: [app] });
    return acc;
  }, []);

  return (
    <main className="rw-dl">
      <section className="rw-dl__head">
        <div className="rw-dl__inner">
          <div className="rw-kicker">Downloads</div>
          <h1>Rift Works apps</h1>
          {/* "published", not "signed": the Windows setup is not code-signed yet (design, 18 Oct 2026) */}
          <p>Direct downloads, published by Rift Works. Open this page on the device you want to install on.</p>
        </div>
      </section>

      {platforms.map((p) => (
        <section className="rw-dl__inner rw-dl__platform" key={p.name}>
          <div className="rw-dl__platform-head">
            <h2>{p.name}</h2>
            <span>{p.apps.length} {p.apps.length === 1 ? 'app' : 'apps'}</span>
          </div>
          {p.apps.map((app) => <AppCard key={app.id} app={app} />)}
        </section>
      ))}
    </main>
  );
}
